import React, { useState, useEffect } from 'react';
import {
  Box, Button, Checkbox, CircularProgress, FormControlLabel, MenuItem, Stack, TextField,
} from '@mui/material';
import { states } from '../Inventory/UsStates';
import PostData from '../../services/PostData.js';
import useFetchWithParams from '../../services/useFetchWithParams.js';
import SnackbarEx from '../Helpers/SnackbarEx.jsx';
import AppDialog from '../ui/AppDialog';

export default function EditVendorInformation({ close, vendId, open = true }) {
  const vendorId = vendId?.vendorId ?? vendId;
  const { data: vendorInfo, load } = useFetchWithParams('api/get-vendor-info', { vendorId });
  const [form, setForm] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [snack, setSnack] = useState({ color: '', message: '', open: false });

  useEffect(() => {
    if (!vendorInfo) return;
    setForm({
      vendor: vendorInfo.vendor || '',
      vendorId: vendorInfo.vendorId,
      street: vendorInfo.street || '',
      city: vendorInfo.city || '',
      zip: vendorInfo.zip || '',
      state: vendorInfo.state || '',
      vendorEmail: vendorInfo.vendorEmail ?? '',
      emailOrdering: vendorInfo.emailOrdering ?? false,
    });
  }, [vendorInfo]);

  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const handleSubmit = async () => {
    if (!form?.vendor?.trim()) {
      setSnack({ color: 'error', message: 'Vendor name is required.', open: true });
      return;
    }
    setSubmitting(true);
    const { success } = await PostData(form, 'api/edit-existing-vendor', false);
    setSubmitting(false);
    if (success) {
      close('success_edit');
    } else {
      setSnack({ color: 'error', message: 'Unable to save vendor changes.', open: true });
    }
  };

  return (
    <AppDialog
      open={open}
      onClose={() => close(false)}
      title={form?.vendor ? `Edit ${form.vendor}` : 'Edit vendor'}
      subtitle="Update supplier details and ordering preferences"
      actions={
        <>
          <Button variant="outlined" onClick={() => close(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={submitting || load || !form}
          >
            {submitting ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Save changes'}
          </Button>
        </>
      }
    >
      {(load || !form) ? (
        <Box sx={{ display: 'grid', placeItems: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Stack spacing={2}>
          <TextField
            label="Vendor name"
            required
            fullWidth
            value={form.vendor}
            onChange={(e) => setField('vendor', e.target.value)}
          />
          <TextField
            label="Street"
            fullWidth
            value={form.street}
            onChange={(e) => setField('street', e.target.value)}
          />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
            <TextField
              label="City"
              fullWidth
              value={form.city}
              onChange={(e) => setField('city', e.target.value)}
            />
            <TextField
              select
              label="State"
              fullWidth
              value={form.state}
              onChange={(e) => setField('state', e.target.value)}
            >
              {states.map((s) => (
                <MenuItem key={s} value={s}>{s}</MenuItem>
              ))}
            </TextField>
            <TextField
              label="ZIP"
              fullWidth
              value={form.zip}
              onChange={(e) => setField('zip', e.target.value)}
            />
          </Stack>
          <FormControlLabel
            control={
              <Checkbox
                checked={Boolean(form.emailOrdering)}
                onChange={(e) => setField('emailOrdering', e.target.checked)}
              />
            }
            label="Enable email ordering"
          />
          {form.emailOrdering && (
            <TextField
              label="Ordering email"
              type="email"
              required
              fullWidth
              placeholder="orders@vendor.com"
              value={form.vendorEmail || ''}
              onChange={(e) => setField('vendorEmail', e.target.value)}
            />
          )}
        </Stack>
      )}

      {snack.open && (
        <SnackbarEx
          color={snack.color}
          message={snack.message}
          open={snack.open}
          close={() => setSnack({ color: '', message: '', open: false })}
        />
      )}
    </AppDialog>
  );
}
